/**
 * TV1-1982 (ADR-061) — the FRONT-END gate against the DOMAIN widget grammar.
 *
 * ## Why this exists
 *
 * The domain/wire grammar (`UiWidgetSchema` / `UiWidgetsLayoutSchema` in
 * `teselly-domain`) is CANONICAL — it is what Cube speaks, what the LLM emits, and
 * what the server validates in TWO places: the PV3 aggregate command
 * (`AddUiWidgetLayoutCommandSchema`) and the LLM output gate
 * (`teselly-ml-and-ai-library` `validateWidgetDefinition`). Until now the front end
 * validated only the BUILDER grammar (`parseWidgetDefinition`, a different shape), so a
 * `toDomainLayout` body was shipped to `AddUiWidgetLayout` with NO local check — an
 * invalid body surfaced only as a swallowed server 400.
 *
 * This module makes the domain schema the front end's validation authority too, WITHOUT
 * importing `@bett3r-dev/jsonschema-definer` (CJS-only, breaks Vite): it validates
 * against the compiled plain JSON Schema shipped through the
 * `@bett3r-dev/teselly-domain/analytics/widget-schema` value subpath.
 *
 * ## Parity with the server — the whole point
 *
 * ajv is the SAME engine the server uses (`@bett3r-dev/pv3` validation). Configured
 * IDENTICALLY here — `allErrors`, `strict: false`, `useDefaults`, `schemaId: '$id'`,
 * `addFormats`, and crucially NO `removeAdditional` (so an unknown key is a hard error,
 * not silently stripped) — validating the SAME schema means "valid on the server <=>
 * valid on the front end" holds by construction. That is the property TV1-1982's
 * cross-validation test pins.
 */

import Ajv, { type ValidateFunction, type ErrorObject } from 'ajv';
import addFormats from 'ajv-formats';
import {
  UI_WIDGET_JSON_SCHEMA,
  UI_WIDGET_LAYOUT_JSON_SCHEMA
} from '@bett3r-dev/teselly-domain/analytics/widget-schema';

/**
 * ajv, configured to MATCH the server verbatim (`pv3/libraries/validation/index.ts`,
 * reproduced in `src/tests/src/analytics/widgetGrammar.adapter.test.ts`). Note what is
 * absent: `removeAdditional` — its absence is why a builder-form `cube` key is a hard
 * rejection here exactly as it is server-side.
 */
const ajv = new Ajv({ allErrors: true, schemaId: '$id', useDefaults: true, strict: false });
addFormats( ajv );

// Compile ONCE. These are the exact objects the server compiles.
const validateWidget: ValidateFunction = ajv.compile( UI_WIDGET_JSON_SCHEMA as Record<string, unknown> );
const validateLayout: ValidateFunction = ajv.compile( UI_WIDGET_LAYOUT_JSON_SCHEMA as Record<string, unknown> );

export type DomainSchemaValidation = {
  valid: boolean;
  /** Human-readable ajv errors — `<instancePath> <message> <params>`. Empty when valid. */
  errors: string[];
};

/** Flatten ajv's error objects into the compact, readable strings the oracle also uses. */
const formatErrors = ( errors: ErrorObject[] | null | undefined ): string[] =>
  ( errors ?? []).map(
    ( error ) => `${ error.instancePath } ${ error.message } ${ JSON.stringify( error.params ) }`.trim()
  );

const runValidation = ( validate: ValidateFunction, value: unknown ): DomainSchemaValidation => {
  const valid = validate( value ) as boolean;
  return { valid, errors: valid ? [] : formatErrors( validate.errors ) };
};

/**
 * Validate ONE domain (wire-form) widget definition against `UiWidgetSchema` — the same
 * authoritative per-widget gate the server's `validateWidgetDefinition` applies. Use for
 * an LLM-generated or curated widget before it is trusted.
 */
export const validateDomainWidget = ( definition: unknown ): DomainSchemaValidation =>
  runValidation( validateWidget, definition );

/**
 * Validate a whole domain layout body against `UiWidgetsLayoutSchema` — the same object
 * `AddUiWidgetLayout` / `ChangeUiWidgetLayout` validate. This is the body
 * `toDomainLayout` produces, so failing here fails BEFORE the command is dispatched
 * rather than as a swallowed 400.
 */
export const validateDomainLayout = ( layout: unknown ): DomainSchemaValidation =>
  runValidation( validateLayout, layout );
